import apiClient from '../../services/apiClient';

// Typed wrappers over backend/src/routes/attendance.js (BP 23), mounted at
// /api/v1/attendance. Student endpoints are requireAuth + requireFeature
// ('class_attendance'); instructor endpoints are requireAuth +
// requireInstructor. This file never reads or sends a `slot`/`ts`/
// `server_time` — the server clock is the only clock that matters here.

export type CheckinMethod = 'code' | 'manual' | 'remote_exception';
export type CheckinFlag = 'fresh_login' | 'new_device' | 'off_campus_network' | 'device_shared' | 'voided';

export interface MyCheckin {
  checked_in_at: string;
  method: CheckinMethod;
}

export interface OpenWindowInfo {
  id: string;
  class_session_id: string;
  session_label: string | null;
  kind: 'end' | 'start';
  closes_at: string;
  server_time: string;
  strict_devices: boolean;
  my_checkin: MyCheckin | null;
}

export interface Checkin {
  id: string;
  window_id: string;
  user_id: string;
  checked_in_at: string;
  method: CheckinMethod;
  slot_offset: number | null;
  note: string | null;
  voided_at: string | null;
  // 0.6.0: additive, optional so every existing fixture in the repo that
  // builds a `Checkin` by hand (there are several, across multiple test
  // files) keeps typechecking without listing it. The server has always sent
  // `null` when there's nothing to report — see AttendanceService.serializeCheckin.
  void_reason?: string | null;
}

export interface MyAttendanceSession {
  class_session_id: string;
  label: string;
  scheduled_date: string | null;
  status: 'present' | 'absent' | 'no_window';
  method: CheckinMethod | null;
}

export interface AttendanceWindowSummary {
  id: string;
  class_session_id: string;
  kind: 'end' | 'start';
  opened_at: string;
  closes_at: string;
  closed_at: string | null;
  rotation_seconds: number;
  strict_devices: boolean;
  status: 'live' | 'closed';
  headcount: number | null;
  checkin_count: number;
  /** Present only on the open response. True when opening this window also
   *  STARTED the class — a window used to be able to go live on a meeting
   *  still marked `planned`, which left every student's /class page reading
   *  "No class today" above an orphan code box. The instructor is told it
   *  happened rather than left to notice. */
  session_started?: boolean;
}

export interface WindowCode {
  code: string;
  slot: number;
  seconds_left: number;
  server_time: string;
  checkin_count: number;
  status: 'live' | 'closed';
}

export interface WindowCheckinRow {
  // Audit note (docs/AUDIT-ATTENDANCE-INTEGRITY.md): this comment used to
  // describe `id` as missing from GET /windows/:id and typed it optional so
  // the Void control could degrade honestly. `AttendanceService.getWindowDetail`
  // already returns `id` on every row (BP 23 changelog 0.2.0 fixed this before
  // this audit) — the comment and the optional type were simply never updated
  // to match. Corrected here; TeachAttendanceWindow's Void button no longer
  // disables itself on a stale assumption.
  id: string;
  user: { id: string; student_id: string | null; name: string };
  checked_in_at: string;
  method: CheckinMethod;
  slot_offset: number | null;
  flags: CheckinFlag[];
  note: string | null;
  failed_attempts: number;
  // 0.6.0: both additive and optional, same reasoning as Checkin.void_reason
  // above — AttendanceService.getWindowDetail has always sent them (voided_at
  // since the 0.2.0 audit fix, void_reason newly), but several test fixtures
  // across the repo build this shape by hand without them and stay valid.
  voided_at?: string | null;
  void_reason?: string | null;
}

export interface WindowDetail {
  window: AttendanceWindowSummary;
  checkins: WindowCheckinRow[];
  headcount: number | null;
  over_headcount: boolean;
}

export interface ReportStudentRow {
  user: { id: string; student_id: string | null; name: string };
  cells: Record<string, string>;
  present_count: number;
  eligible_count: number;
  percent: number;
  manual_count: number;
  remote_count: number;
}

export interface ReportResponse {
  generated_at: string;
  timezone: string;
  sessions: Array<{ id: string; seq: number; label: string; scheduled_date: string | null }>;
  students: ReportStudentRow[];
  notes: string[];
}

// ---- student ----

export async function getOpenWindow(): Promise<OpenWindowInfo | null> {
  const { data } = await apiClient.get<{ window: OpenWindowInfo | null }>('/api/v1/attendance/open');
  return data.window;
}

export async function submitCheckin(payload: { window_id: string; code: string; device_id: string }): Promise<Checkin> {
  const { data } = await apiClient.post<{ checkin: Checkin }>('/api/v1/attendance/checkin', payload);
  return data.checkin;
}

export async function getMyAttendance(): Promise<MyAttendanceSession[]> {
  const { data } = await apiClient.get<{ sessions: MyAttendanceSession[] }>('/api/v1/attendance/me');
  return data.sessions;
}

// ---- instructor ----

export interface OpenWindowPayload {
  class_session_id: string;
  duration_seconds?: number;
  rotation_seconds?: number;
  kind?: 'end' | 'start';
  strict_devices?: boolean;
}

export async function openWindow(payload: OpenWindowPayload): Promise<AttendanceWindowSummary> {
  const { data } = await apiClient.post<{ window: AttendanceWindowSummary }>('/api/v1/attendance/windows', payload);
  return data.window;
}

export async function getWindowCode(windowId: string): Promise<WindowCode> {
  const { data } = await apiClient.get<WindowCode>(`/api/v1/attendance/windows/${windowId}/code`);
  return data;
}

export async function closeWindow(windowId: string, headcount?: number): Promise<AttendanceWindowSummary> {
  const { data } = await apiClient.post<{ window: AttendanceWindowSummary }>(
    `/api/v1/attendance/windows/${windowId}/close`,
    headcount == null ? {} : { headcount },
  );
  return data.window;
}

export async function manualCheckin(
  windowId: string,
  payload: { user_id: string; method: 'manual' | 'remote_exception'; note: string },
): Promise<Checkin> {
  const { data } = await apiClient.post<{ checkin: Checkin }>(`/api/v1/attendance/windows/${windowId}/manual`, payload);
  return data.checkin;
}

export async function voidCheckin(windowId: string, checkinId: string, reason: string): Promise<Checkin> {
  const { data } = await apiClient.post<{ checkin: Checkin }>(
    `/api/v1/attendance/windows/${windowId}/checkins/${checkinId}/void`,
    { reason },
  );
  return data.checkin;
}

export async function getWindowDetail(windowId: string): Promise<WindowDetail> {
  const { data } = await apiClient.get<WindowDetail>(`/api/v1/attendance/windows/${windowId}`);
  return data;
}

export async function listSessionWindows(classSessionId: string): Promise<AttendanceWindowSummary[]> {
  const { data } = await apiClient.get<{ windows: AttendanceWindowSummary[] }>(
    `/api/v1/attendance/sessions/${classSessionId}/windows`,
  );
  return data.windows;
}

export async function getReport(params?: { from_seq?: number; to_seq?: number }): Promise<ReportResponse> {
  const { data } = await apiClient.get<ReportResponse>('/api/v1/attendance/report', { params });
  return data;
}

// GET /report.csv as text (NOT a download — TeachAttendanceReport's actual
// "Export CSV" button goes through lib/downloadBlob.ts's downloadCsv, per
// convention). This is for on-screen rendering only: the JSON /report route
// above never carries the headcount/reconciliation rows (BP 23's own §Reporting
// example shows them only in the CSV layout), and it collapses `one_of_group`
// sessions (e.g. Test 1 Option A/B) into a `group:<name>` cell key that isn't
// exposed anywhere in the JSON response — so a `one_of_group` column can't be
// rendered correctly from /report at all. The CSV's data section already
// contains the fully-resolved, group-aware matrix, so features/attendance/
// reportCsv.ts parses it for both the reconciliation strip and (to sidestep
// the grouping gap) the on-screen student matrix.
export async function getReportCsvText(params?: { from_seq?: number; to_seq?: number }): Promise<string> {
  const { data } = await apiClient.get<string>('/api/v1/attendance/report.csv', {
    params,
    responseType: 'text',
    transformResponse: (res) => res, // keep the raw CSV string — don't let axios try to JSON.parse it
  });
  return data;
}
